import type { WebChatMessage, WebChatMessageToolCall } from '~/types/web-chat'
import type { JsonObject, JsonValue, QuickResearchActivityStep, QuickResearchResultItem } from '~~/shared/toolCalls'
import { activityStatusLabel } from '~/utils/activityStatusLabel'
import { toolActivityFor } from '~~/shared/web-chat/toolActivity'

/** Snippet metadata accumulated for the citation popover. Shared by the store + citation composable. */
export interface CitationSnippetData {
  sectionId: string
  normId: string
  jurisdiction?: string | null
  sectionNumber?: string | null
  displayRef?: string | null
  parentSectionId?: string | null
  parentSectionType?: string | null
  parentSectionNumber?: string | null
  localSectionType?: string | null
  localSectionNumber?: string | null
  canonicalDisplayRef?: string | null
  normTitle: string
  label: string
  snippet: string
  fullText?: string
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

export function normalizeJsonValue(value: unknown): JsonValue {
  if (value === null) return null
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value
  if (Array.isArray(value)) return value.map(normalizeJsonValue)
  if (!isRecord(value)) return String(value)
  const normalized: JsonObject = {}
  for (const [key, entry] of Object.entries(value)) {
    normalized[key] = normalizeJsonValue(entry)
  }
  return normalized
}

export function normalizeJsonObject(value: unknown): JsonObject {
  const normalized = normalizeJsonValue(value)
  return isRecord(normalized) && !Array.isArray(normalized) ? normalized : {}
}

export function isCitationSnippetData(value: unknown): value is CitationSnippetData {
  return isRecord(value)
    && typeof value.sectionId === 'string'
    && typeof value.normId === 'string'
    && typeof value.normTitle === 'string'
    && typeof value.label === 'string'
    && typeof value.snippet === 'string'
    && (value.jurisdiction === undefined || value.jurisdiction === null || typeof value.jurisdiction === 'string')
    && (value.sectionNumber === undefined || value.sectionNumber === null || typeof value.sectionNumber === 'string')
    && (value.displayRef === undefined || value.displayRef === null || typeof value.displayRef === 'string')
    && (value.parentSectionId === undefined || value.parentSectionId === null || typeof value.parentSectionId === 'string')
    && (value.parentSectionType === undefined || value.parentSectionType === null || typeof value.parentSectionType === 'string')
    && (value.parentSectionNumber === undefined || value.parentSectionNumber === null || typeof value.parentSectionNumber === 'string')
    && (value.localSectionType === undefined || value.localSectionType === null || typeof value.localSectionType === 'string')
    && (value.localSectionNumber === undefined || value.localSectionNumber === null || typeof value.localSectionNumber === 'string')
    && (value.canonicalDisplayRef === undefined || value.canonicalDisplayRef === null || typeof value.canonicalDisplayRef === 'string')
    && (value.fullText === undefined || typeof value.fullText === 'string')
}

export function humanizeToolName(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
}

export function quickResearchResultsFromLegacyFindings(findings: unknown[], citations: Record<string, unknown>): JsonObject[] {
  const results: JsonObject[] = []
  for (const [findingIndex, finding] of findings.entries()) {
    if (!isRecord(finding)) continue
    const summary = String(finding.text || '').replace(/\s*\[@[^\]]+\]\(cite:[^)]+\)/g, '').trim()
    const keys = Array.isArray(finding.citations) ? finding.citations.map(String).filter(Boolean) : []
    for (const key of keys) {
      const citation = isRecord(citations[key]) ? citations[key] : {}
      results.push(normalizeJsonObject({
        id: `${String(finding.id || `finding_${findingIndex + 1}`)}_${results.length + 1}`,
        type: String(finding.type || 'rule'),
        sectionId: key,
        normId: String(citation.normId || citation.norm_id || ''),
        jurisdiction: String(citation.jurisdiction || '') || null,
        sectionNumber: String(citation.sectionNumber || citation.section_number || '') || null,
        displayRef: String(citation.displayRef || citation.display_ref || '') || null,
        parentSectionId: String(citation.parentSectionId || citation.parent_section_id || '') || null,
        parentSectionType: String(citation.parentSectionType || citation.parent_section_type || '') || null,
        parentSectionNumber: String(citation.parentSectionNumber || citation.parent_section_number || '') || null,
        localSectionType: String(citation.localSectionType || citation.local_section_type || '') || null,
        localSectionNumber: String(citation.localSectionNumber || citation.local_section_number || '') || null,
        canonicalDisplayRef: String(citation.canonicalDisplayRef || citation.canonical_display_ref || citation.displayRef || citation.display_ref || '') || null,
        label: String(citation.label || citation.normTitle || citation.norm_title || key),
        fullText: String(citation.fullText || citation.full_text || citation.snippet || ''),
        quote: String(citation.snippet || citation.fullText || citation.full_text || ''),
        summary,
      }))
    }
  }
  return results
}

export function normalizeToolOutput(value: unknown): WebChatMessageToolCall['output'] {
  if (value === undefined) return undefined
  if (value === null) return value
  if (typeof value === 'string') {
    return normalizeQuickResearchOutputString(value) || value
  }
  return value as WebChatMessageToolCall['output']
}

export function normalizeQuickResearchOutputString(value: string): WebChatMessageToolCall['output'] | null {
  try {
    const parsed = JSON.parse(value)
    if (!isRecord(parsed)) return null
    const packet = isRecord(parsed.packet) ? parsed.packet : {}
    const isQuickResult = parsed.kind === 'run_query_legal_catalog_result'
    const isLegacyPacket = parsed.kind === 'query_legal_catalog_packet'
    if (!isQuickResult && !isLegacyPacket) return null

    const citations = isRecord(parsed.citations)
      ? parsed.citations
      : isRecord(packet.citations) ? packet.citations : {}
    const results = Array.isArray(parsed.results)
      ? parsed.results
      : Array.isArray(packet.results) ? packet.results : []
    const readSectionIds = Array.isArray(parsed.read_section_ids)
      ? parsed.read_section_ids
      : Array.isArray(packet.read_section_ids) ? packet.read_section_ids : []
    const readCitations = Array.isArray(parsed.read_citations)
      ? parsed.read_citations
      : Array.isArray(packet.read_citations) ? packet.read_citations : []
    const researchedQueries = Array.isArray(parsed.researched_queries)
      ? parsed.researched_queries
      : Array.isArray(packet.researched_queries) ? packet.researched_queries : []
    const findings = Array.isArray(parsed.findings)
      ? parsed.findings
      : Array.isArray(packet.findings) ? packet.findings : []
    return {
      kind: 'run_query_legal_catalog_result',
      status: parsed.status === 'answered' || parsed.status === 'escalation_recommended' || parsed.status === 'insufficient_evidence'
        ? parsed.status
        : packet.status === 'answered' || packet.status === 'escalation_recommended' || packet.status === 'insufficient_evidence'
          ? packet.status
          : 'answered',
      // normalizeJsonValue deep-copies to JSON-safe values, preserving the result-item shape.
      results: normalizeJsonValue(results.length > 0 ? results : quickResearchResultsFromLegacyFindings(findings, citations)) as unknown as QuickResearchResultItem[],
      unresolved_gaps: normalizeJsonValue(Array.isArray(parsed.unresolved_gaps) ? parsed.unresolved_gaps : Array.isArray(packet.unresolved_gaps) ? packet.unresolved_gaps : []),
      researched_queries: normalizeJsonValue(researchedQueries),
      read_section_ids: normalizeJsonValue(readSectionIds),
      read_citations: normalizeJsonValue(readCitations),
      escalation: normalizeJsonValue(isRecord(parsed.escalation) ? parsed.escalation : isRecord(packet.escalation) ? packet.escalation : {}),
      activity_summary: [
        ...(researchedQueries.length > 0 ? [{ type: 'search', label: `Searched ${researchedQueries.length} legal topic${researchedQueries.length === 1 ? '' : 's'}`, status: 'completed' as const }] : []),
        ...(readSectionIds.length + readCitations.length > 0 ? [{ type: 'read', label: `Read ${readSectionIds.length + readCitations.length} legal provision${readSectionIds.length + readCitations.length === 1 ? '' : 's'}`, status: 'completed' as const }] : []),
        ...(results.length + findings.length > 0 ? [{ type: 'sources', label: 'Prepared cited legal findings', status: 'completed' as const }] : []),
      ],
      ...(parseQuickResearchActivitySteps(parsed.activity).length > 0
        ? { activity_steps: parseQuickResearchActivitySteps(parsed.activity) }
        : {}),
    }
  } catch {
    return null
  }
}

export function parseQuickResearchActivitySteps(value: unknown): QuickResearchActivityStep[] {
  if (!Array.isArray(value)) return []
  const steps: QuickResearchActivityStep[] = []
  for (const entry of value) {
    if (!isRecord(entry)) continue
    const kind = entry.kind === 'read' ? 'read' : entry.kind === 'search' ? 'search' : null
    const label = typeof entry.label === 'string' ? entry.label : ''
    if (!kind || !label) continue
    steps.push({ kind, label, ...(typeof entry.detail === 'string' ? { detail: entry.detail } : {}) })
  }
  return steps
}

export function normalizeWebChatToolCall(value: unknown): WebChatMessageToolCall {
  const record = isRecord(value) ? value : {}
  const name = typeof record.name === 'string' ? record.name : 'unknown_tool'
  return {
    id: typeof record.id === 'string' || typeof record.id === 'number' ? record.id : undefined,
    callId: typeof record.callId === 'string' ? record.callId : undefined,
    toolCallRowId: typeof record.toolCallRowId === 'string' ? record.toolCallRowId : undefined,
    name,
    input: normalizeJsonValue(record.input ?? {}),
    output: normalizeToolOutput(record.output),
    activity: Array.isArray(record.activity) ? record.activity as WebChatMessageToolCall['activity'] : undefined,
    subagentSteps: Array.isArray(record.subagentSteps) ? record.subagentSteps.map(String).filter(Boolean) : undefined,
    status: record.status === 'running' || record.status === 'completed' || record.status === 'failed' ? record.status : undefined,
    segmentIndex: typeof record.segmentIndex === 'number' ? record.segmentIndex : 0,
    startedAt: typeof record.startedAt === 'string' ? record.startedAt : undefined,
    completedAt: typeof record.completedAt === 'string' || record.completedAt === null ? record.completedAt : undefined,
  }
}

export function matchesProviderToolCall(call: WebChatMessageToolCall, providerToolCallId: string): boolean {
  return call.id === providerToolCallId || call.callId === providerToolCallId
}

export function sameToolCall(a: WebChatMessageToolCall, b: WebChatMessageToolCall): boolean {
  return (!!a.id && a.id === b.id) || (!!a.callId && a.callId === b.callId)
}

export function mergeTopLevelToolCalls(next: WebChatMessageToolCall[], previous: WebChatMessageToolCall[]): WebChatMessageToolCall[] {
  // Each `tool_calls` wire event carries only THAT ADK event's calls (a per-round delta, not a
  // full-turn snapshot — see chatStream.ts), so union onto the accumulator: update matching calls in
  // place, append new ones, and keep earlier rounds' calls that this delta doesn't re-send. Returning
  // `next` alone would drop every top-level call from a prior round.
  const merged = [...previous]
  for (const call of next) {
    const idx = merged.findIndex(prev => sameToolCall(prev, call))
    if (idx === -1) {
      merged.push(call)
      continue
    }
    // Preserve fields a later `tool_calls` delta doesn't re-send (sub-agent steps + the
    // quick-research activity steps buffered from the `tool_result` event).
    const existing = merged[idx]!
    const preserved: Partial<WebChatMessageToolCall> = {}
    if (existing.subagentSteps?.length && !call.subagentSteps?.length) preserved.subagentSteps = existing.subagentSteps
    if (existing.activitySteps?.length && !call.activitySteps?.length) preserved.activitySteps = existing.activitySteps
    merged[idx] = Object.keys(preserved).length > 0 ? { ...call, ...preserved } : call
  }
  return merged
}

export function appendSubagentStep(calls: WebChatMessageToolCall[], parentCallId: string, step: string): WebChatMessageToolCall[] {
  return calls.map(call => {
    if (!matchesProviderToolCall(call, parentCallId)) return call
    const steps = call.subagentSteps || []
    if (steps.includes(step)) return call
    return { ...call, subagentSteps: [...steps, step] }
  })
}

type TranslateFn = (key: string, params?: Record<string, unknown>) => string

export function nestedToolCallLabel(t: TranslateFn, value: unknown): string | null {
  if (!isRecord(value) || typeof value.name !== 'string') return null
  const input = isRecord(value.input) ? value.input : {}
  if (value.name === 'query_legal_catalog') {
    const queries = Array.isArray(input.queries) ? input.queries : undefined
    const queryCount = queries ? queries.length : input.query ? 1 : 0
    const first = queries?.[0]
    // The search term lives in `queries[].texto` (structured) or a bare `query` string.
    const query = typeof input.query === 'string' ? input.query
      : (isRecord(first) && typeof first.texto === 'string') ? first.texto
        : (isRecord(first) && typeof first.text === 'string') ? first.text
          : undefined
    return activityStatusLabel(t, toolActivityFor(value.name).code, { count: queryCount, query })
  }
  if (value.name === 'read_legal_details') {
    const count = (Array.isArray(input.ids) ? input.ids.length : 0) + (Array.isArray(input.citations) ? input.citations.length : 0)
    return activityStatusLabel(t, toolActivityFor(value.name).code, { count })
  }
  return humanizeToolName(value.name)
}

export function subagentStepLabel(t: TranslateFn, event: unknown): string | null {
  if (!isRecord(event)) return null
  if (event.type === 'status') {
    const message = typeof event.message === 'string' ? event.message.replace(/\.{3}$/, '') : ''
    const code = typeof event.code === 'string' ? event.code : undefined
    if (!code && !message) return null
    return activityStatusLabel(t, code, {}, message)
  }
  if (event.type === 'tool_calls' && Array.isArray(event.calls)) {
    return event.calls.map(call => nestedToolCallLabel(t, call)).filter(Boolean).join('; ') || null
  }
  if (event.type === 'tool_result') {
    return typeof event.name === 'string'
      ? `${humanizeToolName(event.name)} completed`
      : null
  }
  return null
}

export function normalizeValidationClaims(value: any): NonNullable<NonNullable<WebChatMessage['validation']>['claims']> {
  return Array.isArray(value)
    ? value.map((claim: any) => ({
      claim: String(claim?.claim || ''),
      citations: Array.isArray(claim?.citations) ? claim.citations.map(String) : [],
      citationLabels: Array.isArray(claim?.citationLabels) ? claim.citationLabels.map(String).filter(Boolean) : undefined,
      verdict: ['supported', 'unsupported', 'unclear', 'missing_citation', 'not_legal_claim'].includes(claim?.verdict)
        ? claim.verdict
        : 'unclear',
      reason: String(claim?.reason || ''),
      severity: ['high', 'medium', 'low', 'none'].includes(claim?.severity) ? claim.severity : 'none',
    })).filter((claim: any) => claim.claim)
    : []
}

export function normalizeValidationResult(result: any): WebChatMessage['validation'] {
  const status = ['not_applicable', 'running', 'passed', 'warning', 'unavailable'].includes(result?.status)
    ? result.status
    : 'unavailable'
  const checkedClaims = normalizeValidationClaims(result?.checkedClaims)
  const failedClaims = normalizeValidationClaims(result?.failedClaims ?? result?.claims)
  return {
    status,
    checkedAt: result?.checkedAt ? String(result.checkedAt) : undefined,
    summary: result?.summary ? String(result.summary) : undefined,
    checkedClaims: checkedClaims.length > 0 ? checkedClaims : undefined,
    failedClaims: failedClaims.length > 0 ? failedClaims : undefined,
    claims: failedClaims.length > 0 ? failedClaims : undefined,
  }
}

export function stripLegalCitationMetadata(block: string): string {
  return block
    .replace(/^Section ID:\s*[a-f0-9-]{36}\s*$/gim, '')
    .replace(/^Norm ID:\s*[a-f0-9-]{36}\s*$/gim, '')
    .replace(/^Jurisdiction:\s*[A-Za-z]{2}\s*$/gim, '')
    .replace(/^Display ref:\s*.+?\s*$/gim, '')
    .replace(/^Section number:\s*.+?\s*$/gim, '')
    .replace(/^Parent section ID:\s*[a-f0-9-]{36}\s*$/gim, '')
    .replace(/^Parent section type:\s*.+?\s*$/gim, '')
    .replace(/^Parent section number:\s*.+?\s*$/gim, '')
    .replace(/^Local section type:\s*.+?\s*$/gim, '')
    .replace(/^Local section number:\s*.+?\s*$/gim, '')
    .trim()
}
